import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, User, Settings, Sparkles, Volume2, VolumeX, CreditCard, BarChart3, Smartphone } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { DashboardViewSwitcher } from "@/components/admin/DashboardViewSwitcher";
import { useSound } from "@/hooks/useSound";
import { usePremiumAccess } from "@/hooks/usePremiumAccess";
import { useState, useEffect } from "react";
import { KidsAiLogo } from "@/components/brand/KidsAiLogo";

export const Navigation = () => {
  const { user, signOut, userRoles } = useAuth();
  const navigate = useNavigate();
  const { toggleMute, isMuted } = useSound();
  const { tier } = usePremiumAccess();
  const [muted, setMuted] = useState(false);

  // Load mute state from localStorage on mount
  useEffect(() => {
    const savedMute = localStorage.getItem('sounds-muted');
    setMuted(savedMute === 'true');
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const handleMuteToggle = () => {
    const newMutedState = toggleMute();
    setMuted(newMutedState);
    localStorage.setItem('sounds-muted', String(newMutedState));
  };

  const isSuperAdmin = userRoles.includes("super_admin");
  const isParent = userRoles.includes("parent");
  const showPremiumBadge = isParent && tier === 'free';

  return (
    <nav className="border-b-4 border-white bg-gradient-to-r from-kid-blue/20 via-kid-purple/20 to-kid-pink/20 backdrop-blur-lg shadow-xl sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between gap-2 sm:gap-3 h-16 sm:h-20 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center min-w-0 flex-shrink">
            <KidsAiLogo className="h-9 sm:h-12 md:h-14 w-auto max-w-full" />
          </Link>

          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {isSuperAdmin && <DashboardViewSwitcher />}

            {/* Show Pricing link for non-logged-in users */}
            {!user && (
              <Link to="/pricing" className="hidden sm:inline-flex">
                <Button variant="ghost" className="font-fredoka">
                  Pricing
                </Button>
              </Link>
            )}

            {/* Show Premium Upgrade for free tier parents (hidden for admins) */}
            {showPremiumBadge && !isSuperAdmin && (
              <Button
                variant="default"
                size="sm"
                className="gap-2 bg-gradient-to-r from-primary to-secondary h-10 sm:h-11"
                onClick={() => navigate("/parent")}
                aria-label="Upgrade to Premium"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Upgrade to Premium</span>
              </Button>
            )}
            
            {/* Install App Button */}
            <Link to="/install" className="hidden md:inline-flex">
              <Button
                variant="ghost"
                size="icon"
                className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-white/50 hover:border-white shadow-md hover:shadow-xl transform hover:scale-110 transition-all"
                title="Install App"
              >
                <Smartphone className="h-5 w-5 text-kid-blue" />
              </Button>
            </Link>
            
            {/* Sound Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMuteToggle}
              className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-white/50 hover:border-white shadow-md hover:shadow-xl transform hover:scale-110 transition-all"
              title={muted ? "Unmute sounds" : "Mute sounds"}
            >
              {muted ? (
                <VolumeX className="h-5 w-5 text-gray-500" />
              ) : (
                <Volume2 className="h-5 w-5 text-kid-purple" />
              )}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-full border-4 border-white shadow-lg hover:shadow-xl transform hover:scale-110 transition-all p-0"
                >
                  <Avatar className="h-full w-full">
                    <AvatarFallback className="bg-gradient-to-br from-kid-purple to-kid-pink text-white font-bubblegum text-xl">
                      {user?.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-64 p-4 border-4 border-white shadow-2xl rounded-2xl bg-popover"
              >
                <DropdownMenuLabel>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 border-2 border-kid-purple">
                      <AvatarFallback className="bg-gradient-primary text-white font-fredoka">
                        {user?.email?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-fredoka font-bold text-gray-800">{user?.email}</p>
                      <p className="text-xs font-comic text-gray-500">Learning Champion! 🏆</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-3" />
                <DropdownMenuItem className="p-3 rounded-xl hover:bg-kid-purple/10 font-fredoka">
                  <User className="mr-3 h-5 w-5 text-kid-purple" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/analytics')}
                  className="p-3 rounded-xl hover:bg-kid-green/10 font-fredoka"
                >
                  <BarChart3 className="mr-3 h-5 w-5 text-kid-green" />
                  Learning Analytics
                </DropdownMenuItem>
                <DropdownMenuItem className="p-3 rounded-xl hover:bg-kid-blue/10 font-fredoka">
                  <Settings className="mr-3 h-5 w-5 text-kid-blue" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-3" />
                <DropdownMenuItem 
                  onClick={handleSignOut}
                  className="p-3 rounded-xl hover:bg-red-50 font-fredoka text-red-600"
                >
                  <LogOut className="mr-3 h-5 w-5" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};
